import { useParams, useNavigate, Link } from "react-router-dom";
import { useAdminOrg } from "../../hooks/use-admin.js";
import { PageSkeleton } from "../../components/skeleton.js";

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return "\u2014";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-IE");
}

function formatPrice(cents: number): string {
  return `\u20AC${(cents / 100).toFixed(2)}`;
}

export function AdminOrgDetailPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useAdminOrg(orgId);

  if (isLoading) return <PageSkeleton />;
  if (error || !data) return <div className="alert alert--error">{error instanceof Error ? error.message : "Org not found."}</div>;

  return (
    <div className="product-page">
      <button
        type="button"
        className="btn btn--secondary btn--sm mb-3"
        onClick={() => navigate("/admin/orgs")}
      >
        &larr; Back to orgs
      </button>
      <h1 className="page-title">{data.name}</h1>

      <section className="product-section">
        <h2 className="section-title">Summary</h2>
        <div className="stat-grid">
          <div className="stat-item">
            <span className="stat-item__value">{data.name}</span>
            <span className="stat-item__label">Name</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">{data.country}</span>
            <span className="stat-item__label">Country</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {data.plan ? (
                <Link to={`/admin/plans/${data.plan.id}`}>{data.plan.name}</Link>
              ) : (
                "\u2014"
              )}
            </span>
            <span className="stat-item__label">Plan</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {data.plan ? formatPrice(data.plan.priceEurCents) : "\u2014"}
            </span>
            <span className="stat-item__label">Plan Price</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {data.websitesUsed} / {data.websitesLimit}
            </span>
            <span className="stat-item__label">Websites</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {data.scansUsedThisMonth} / {data.scansLimitPerMonth}
            </span>
            <span className="stat-item__label">Scans This Month</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {data.subscriptionStatus || "\u2014"}
            </span>
            <span className="stat-item__label">Status</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {relativeTime(data.lastActivityAt)}
            </span>
            <span className="stat-item__label">Last Activity</span>
          </div>
        </div>
      </section>

      <section className="product-section">
        <h2 className="section-title">
          Websites ({data.websites?.length ?? 0})
        </h2>
        {data.websites && data.websites.length > 0 ? (
          <div className="table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Score</th>
                  <th>Verified</th>
                </tr>
              </thead>
              <tbody>
                {data.websites.map(
                  (site: {
                    id: string;
                    url: string;
                    domain: string;
                    latestScore: number | null;
                    verified: boolean;
                  }) => (
                    <tr key={site.id}>
                      <td>
                        <Link
                          to={`/admin/websites/${site.id}`}
                          className="text-sm"
                        >
                          {site.domain || site.url}
                        </Link>
                      </td>
                      <td className="text-sm">
                        {site.latestScore != null
                          ? `${site.latestScore}%`
                          : "\u2014"}
                      </td>
                      <td className="text-sm">
                        {site.verified ? "\u2713" : "\u2014"}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted text-sm">No websites yet.</p>
        )}
      </section>

      <section className="product-section">
        <h2 className="section-title">
          Members ({data.members?.length ?? 0})
        </h2>
        {data.members && data.members.length > 0 ? (
          <div className="table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Admin</th>
                </tr>
              </thead>
              <tbody>
                {data.members.map(
                  (m: {
                    userId: string;
                    email: string;
                    role: string;
                    isAdmin: boolean;
                  }) => (
                    <tr key={m.userId}>
                      <td>
                        <Link
                          to={`/admin/users/${m.userId}`}
                          className="text-sm"
                        >
                          {m.email}
                        </Link>
                      </td>
                      <td className="text-sm text-capitalize">{m.role}</td>
                      <td className="text-sm">
                        {m.isAdmin ? "Yes" : "\u2014"}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted text-sm">No members.</p>
        )}
      </section>

      {data.warnings?.length > 0 && (
        <section className="product-section warning-border">
          <h2 className="section-title">Warnings</h2>
          <ul>
            {data.warnings.map((w, i) => (
              <li key={i} className="mb-2">
                {w}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
